import React from "react";

import type { CypressLocatorsProps } from "@daytrip/cypress-utils";
import { extractCypressLocatorDataCyAttribute } from "@daytrip/cypress-utils";
import { Colors } from "@daytrip/horizon-util-ts-tokens";
import { isNotUndefinedOrNull } from "@daytrip/utils";
import styled, { css } from "styled-components";

import { Breakpoint } from "../utils/Breakpoint";
import type { Dimensions } from "../Dimensions";
import { transformBreakpointToCSSMediaQuery } from "../utils/transformers/transformBreakpointToCSSMediaQuery";

const StyledInput = styled.input<{
    isValid?: boolean;
    readOnly?: boolean;
    generalSpacing?: Dimensions;
    mobileSpacing?: Dimensions;
}>`
    margin-bottom: ${({ generalSpacing }) => generalSpacing};
    border-color: ${({ isValid }) => isNotUndefinedOrNull(isValid) && isValid === false && Colors.Red400};

    ${({ readOnly }) =>
        readOnly &&
        css`
            cursor: not-allowed;

            :focus {
                border-color: ${Colors.Grey200};
            }
        `};

    ${({ mobileSpacing }) =>
        isNotUndefinedOrNull(mobileSpacing) &&
        css`
            ${transformBreakpointToCSSMediaQuery(Breakpoint.sm, "down")} {
                margin-bottom: ${mobileSpacing};
            }
        `}
`;

export interface IInputProps extends CypressLocatorsProps {
    type?: "text" | "number" | "tel" | "password" | "email" | "search";
    disabled?: boolean;
    min?: number;
    max?: number;
    value?: number | string;
    defaultValue?: number | string;
    placeholder?: string;
    required?: boolean;
    readOnly?: boolean;
    pattern?: string;
    spacing?: Dimensions;
    mobileSpacing?: Dimensions;
    className?: string;
    name?: string;
    id?: string;
    onChange?: (value: string) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    tabIndex?: number;
    isValid?: boolean;
    autoComplete?: string;
    isAutocompleteDisabled?: boolean;
    inputRef?: ((instance: HTMLInputElement | null) => void) | React.MutableRefObject<HTMLInputElement | null>;
    maxLength?: number;
    ariaLabel?: string;
    onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
    ariaInvalid?: boolean;
    ariaLive?: "assertive" | "off" | "polite";
    ariaBusy?: boolean;
}

export const Input: React.FC<React.PropsWithChildren<IInputProps>> = ({
    type = "text",
    disabled,
    min,
    max,
    value,
    placeholder,
    required,
    readOnly,
    pattern,
    spacing,
    mobileSpacing,
    className,
    id,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    tabIndex,
    isValid,
    autoComplete,
    isAutocompleteDisabled,
    name,
    inputRef,
    maxLength,
    defaultValue,
    ariaLabel,
    onPaste,
    ariaInvalid,
    ariaBusy,
    ariaLive,
    ...restProps
}) => (
    <StyledInput
        autoComplete={isAutocompleteDisabled ? "off" : autoComplete}
        generalSpacing={spacing}
        mobileSpacing={mobileSpacing}
        isValid={isValid}
        id={id}
        name={name || id}
        type={type}
        className={className}
        disabled={disabled}
        placeholder={placeholder}
        required={required}
        pattern={pattern}
        min={min}
        max={max}
        value={value}
        defaultValue={defaultValue}
        onChange={
            isNotUndefinedOrNull(onChange)
                ? (event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)
                : undefined
        }
        onPaste={onPaste}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        tabIndex={tabIndex}
        readOnly={readOnly}
        ref={inputRef}
        maxLength={maxLength}
        data-form-type="other"
        data-lpignore="true"
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        aria-busy={ariaBusy}
        aria-live={ariaLive}
        {...extractCypressLocatorDataCyAttribute(restProps)}
    />
);

Input.displayName = "Input";
